<template>
  <div class="container">

    <div class="row">
      <div class="col 2-col">
        <h2 id="administrative">1. General Information</h2>
        <h3 >1.1. Administrative</h3>
      </div>
    </div>
    <div class="row">
      <div class="col col-8">
        <ImageMap 
          title="Administrative Overview" 
          v-bind:images="[{'title': 'Administrative Overview', 'source': '/mountain_summary/Administrative.png'}]" 
          v-bind:location="location"
          v-bind:factsheet="factsheet" 
          v-bind:loaded="loaded"
          attribution="Data: GADM, GeoNames.">
            
        </ImageMap>
      </div>
      <div class="col col-4">
        <table class="table table-sm">
          <caption>Overlapping Countries</caption>
          <tr>
            <th>Name</th><th>Area</th>
          </tr>
          <tr v-for="country in factsheet.data.mountain_summary.basemap_data.admin_overlap">
            <td>{{country.name}}</td>
            <td>{{country.intersect_percent | percent}}</td>
          </tr>

        </table>
        <table class="table table-sm">
          <caption>Largest Cities</caption>
          <tr>
            <th>Country</th>
          </tr>
          <tr v-for="place in factsheet.data.mountain_summary.basemap_data.populated_places.slice(0, 5)">
            <td>{{place.name}} ({{place.country}})</td>
          </tr>
        </table>

      </div>
    </div>

    <div class="row">
      <div class="col">
        <h3 >1.2. Demographics</h3>
        <p>
        It is estimated that approximately <strong>{{ factsheet.data.mountain_summary.ghs_pop.pop_2020_tot | abbrev}} people</strong> live in the area in 2020. This is expected to 

        <strong>{{ factsheet.data.mountain_summary.ghs_pop.pop_2030_tot > factsheet.data.mountain_summary.ghs_pop.pop_2020_tot ? "increase": "decrease"}} to {{ factsheet.data.mountain_summary.ghs_pop.pop_2030_tot | abbrev }} by 2030</strong>.

        The largest cities are <strong>{{ largestCities}}</strong>.

        </p>
      </div>
    </div>

    <div class="row">
      <div class="col col-8">
        <ImageMap 
          title="Population Density" 
          v-bind:images="[{'title': 'Population Density', 'source': '/mountain_summary/PopulationDensity.png'}]" 
          v-bind:location="location"
          v-bind:factsheet="factsheet" 
          v-bind:loaded="loaded"
          attribution="Data: GADM, GeoNames.">
            
        </ImageMap>
      </div>
      <div class="col col-4">
        <Figure 
          title="Population Estimates 1975-2030" 
          src="mountain_summary/PopulationTotals.svg"
          label="Population estimates for 5 year periods from 1975 until 2030. Figures until 2020 are estimates, whereas figures after 2020 are predictions." 
          attribution="Data: GHS-POP."

          v-bind:location="location"
          v-bind:factsheet="factsheet" 
          v-bind:loaded="loaded"
          >
        </Figure>

      </div>
    </div>
    <!--
    <div class="row">
      
      <div class="col col-4">

        <Figure 
          title="Population Changes 1975-2030" 
          src="mountain_summary/PopulationChange.svg"
          label="Population changes in percent per 5 year period from 1975 until 2030. Figures until 2020 are estimates, whereas figures after 2020 are predictions." 
          v-bind:location="location"
          v-bind:factsheet="factsheet" 
          v-bind:loaded="loaded"
          attribution="Data: GHS-POP.">
        </Figure>

      </div>
    </div>
    -->


    <div class="row">
      <div class="col">
        <h3 >1.3. Development and Economic Indicators</h3>
        <p>
          The Human Development Index (HDI) is determined by a combination of indicators such as life expectancy, literacy rate, access to electricity, GDP, and many more. The average HDI 
          in this mountain range is estimated to be <strong>{{ factsheet.data.mountain_summary.hdi_gdp.hdi_2015_avg | fixed2 }} in 2015</strong>. 
          This is considered to be a 
          <strong>
            <template v-if="factsheet.data.mountain_summary.hdi_gdp.hdi_2015_avg > 0.8">very high</template>
            <template v-else-if="factsheet.data.mountain_summary.hdi_gdp.hdi_2015_avg > 0.7">high</template>
            <template v-else-if="factsheet.data.mountain_summary.hdi_gdp.hdi_2015_avg > 0.55">medium</template>
            <template v-else-if="factsheet.data.mountain_summary.hdi_gdp.hdi_2015_avg > 0">low</template>
          </strong> level of development. 
        </p>
        <p>
          The total Gross Domestic Product (GDP) within this area was estimated to be <strong>${{ factsheet.data.mountain_summary.hdi_gdp.gdp_2015_sum | abbrev}} in 2015</strong>, having <strong>
          {{ factsheet.data.mountain_summary.hdi_gdp.gdp_2015_sum > factsheet.data.mountain_summary.hdi_gdp.gdp_2000_sum ? "increased" : "decreased"}} by an estimated 

          ${{ (factsheet.data.mountain_summary.hdi_gdp.gdp_2015_sum - factsheet.data.mountain_summary.hdi_gdp.gdp_2000_sum) | abbrev  }}</strong>


            since 2000.
        </p>
        <p>The table below shows an overview of total GDP and HDI estimates for the mountain range.</p>
      </div>
    </div>
    <div class="row">
      <div class="col col-10 offset-1">
        <table class="table table-sm">
        <caption>Table X. Estimates of Development and Economic Indicators</caption>
        <tr>
          <th>Indicator</th>
          <th>1990</th>
          <th>2000</th>
          <th>2015</th>
        </tr>
        <tr>
          <td>1. Total Gross Domestic Product (GDP)</td>
          <td>${{ factsheet.data.mountain_summary.hdi_gdp.gdp_1990_sum | abbrev}}</td>
          <td>${{ factsheet.data.mountain_summary.hdi_gdp.gdp_2000_sum | abbrev}}</td>
          <td>${{ factsheet.data.mountain_summary.hdi_gdp.gdp_2015_sum | abbrev}}</td>
        </tr>
        <tr>
          <td>2. Average Human Development Index (HDI)</td>
          <td>{{ factsheet.data.mountain_summary.hdi_gdp.hdi_1990_avg | fixed2 }}</td>
          <td>{{ factsheet.data.mountain_summary.hdi_gdp.hdi_2000_avg | fixed2 }}</td>
          <td>{{ factsheet.data.mountain_summary.hdi_gdp.hdi_2015_avg | fixed2 }}</td>
        </tr>
        </table>
      </div>


    </div>

    <div class="row">
      <div class="col col-8">
        <ImageMap 
          title="Travel Time to Nearest City" 
          v-bind:images="[{'title': 'Access Time to Cities', 'source': '/mountain_summary/AccessTimes.png'}]" 
          v-bind:location="location"
          v-bind:factsheet="factsheet" 
          v-bind:loaded="loaded"
          attribution="Data:">
            
        </ImageMap>
      </div>
    </div>

    <div class="row">
      <div class="col">
        <h3 >1.4. Protected Areas</h3>
        <p>The map below shows all terrestrial protected areas within the mountain range from the World Database of Protected Areas (WDPA). All the protected areas shown in the map cover a total of <strong>{{ factsheet.data.mountain_summary.protected_areas_area | km2 }}</strong>, which is <strong>{{ 100 * (factsheet.data.mountain_summary.protected_areas_area / factsheet.data.mountain_summary.total_area) | percent }}</strong> of the total area of the mountain range.</p>
      </div>
    </div>

    <div class="row">

      <div class="col col-8">
        <ImageMap 
          title="Protected Areas" 
          v-bind:images="[{title:'test',source:'/mountain_summary/ProtectedAreas.png'}]" 
          v-bind:location="location"
          v-bind:factsheet="factsheet" 
          v-bind:loaded="loaded"
          attribution="Data: World Database of Protected Areas.">
        </ImageMap>
      </div>

      <div class="col col-4">
        <table class="table table-sm">
          <caption>Table X. 10 Largest Protected Areas</caption>
          <tr>
            <th>Name</th>
          </tr>
          <tr v-for="area in factsheet.data.mountain_summary.protected_areas_main">
            <td>{{area.name}}<br/>
            <small>{{area.type}}</small></td>
          </tr>
        </table>


      </div>  


    </div>


  </div>

</template>

<script>
    /* eslint-disable */
    import ImageMap from '@/components/factsheet/maps/ImageMap.vue'
    import LandDegradationNeutrality from '@/components/factsheet/LandDegradationNeutrality.vue'
    import Figure from '@/components/factsheet/Figure.vue'

    import _ from 'lodash';
    export default {
        name: 'Summary',
        components: { ImageMap, Figure, LandDegradationNeutrality },
        props: ['location', 'factsheet', 'loaded'],
        data() {
            return {
            }
        },
        created() {
        },
        computed: {
          largestCities() {
            return this.andList(this.factsheet.data.mountain_summary.basemap_data.populated_places.slice(0,4), "name")
          },
          ldr() {
            if(this.loaded && this.factsheet.data["land_degradation_neutrality"]) {
              return this.factsheet.data["land_degradation_neutrality"]
            } else {
              return null;
            }
          }
        },
        mounted() {
        },
        filters: {
          percent: function (value) {
            return value.toFixed(1)+'%'
          },
          fixed2: function(value) {
            return value.toFixed(2)
          },
          km2: function(value) {
            return parseInt(value/1000000)+" km²"
          },
          abbrev: function(value) {
            if(value > 1000000000000) {
              return (value/1000000000000).toFixed(1)+"tn"
            }
            else if(value > 1000000000) {
              return (value/1000000000).toFixed(1)+"bn"
            }
            else if(value > 1000000) {
              return (value/1000000).toFixed(1)+"m"
            }
            else if(value > 1000) {
              return (value/1000).toFixed(1)+"k"
            }
            else {
              return value.toFixed(0)
            }
          },
        },
        methods: {
          urlFor(asset) {
            return process.env.VUE_APP_API+'/api/data/factsheet/'+this.factsheet.info.identifier+'/data/'+asset;
          },
          andList(list, attr, attrpercent) {
            var ret = ""
            for(var i=0; i<list.length;i++) {
              var percent = attrpercent ? ' ('+list[i][attrpercent]+'%)' : ''
              if (list.length == 1) {
                return list[i][attr]+percent
              }
              if(i == list.length-1) {
                ret += " and "+list[i][attr]+percent
              } else {
                ret += list[i][attr]+percent+", "
              }
            }
            return ret
          }
        }
    }
</script>

<style>
div.map {
    width:100%;
    height:450px;
    display:block;
}
</style>

<style scoped>

caption {
  caption-side: top;
  font-weight:bold;
  font-size:1em;
  padding:5px;
  color:rgb(33, 37, 41);
  padding-left:0px;
}
table {
   font-size:0.75em;
}
table th, table td {
  padding-left:0px;
}

</style>